import React, { useEffect, useRef, useState } from 'react';
import { FishingScore, FishingFactor, DailyWeather } from '../models/WeatherData';
import Chart from 'chart.js/auto';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { db } from '../services/FishingDB';

interface FishingForecastProps {
  fishingScores: FishingScore[];
  dailyFishingScores: FishingScore[];
  dailyWeather: DailyWeather[];
  selectedDate: string;
}

const FishingForecast: React.FC<FishingForecastProps> = ({
  fishingScores,
  dailyFishingScores,
  dailyWeather,
  selectedDate
}) => {
  const { t } = useLanguage();
  const { defaultSpecies } = useSettings();
  const [speciesDisplayName, setSpeciesDisplayName] = useState<string>('');
  const chartRef = useRef<HTMLCanvasElement>(null);
  const dailyChartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);
  const dailyChartInstanceRef = useRef<Chart | null>(null);

  // Load species display name from database
  useEffect(() => {
    const loadSpeciesName = async () => {
      try {
        const species = await db.species.where('key').equals(defaultSpecies).first();
        if (species) {
          // If custom, use key directly; if default, translate
          const displayName = species.isCustom ? species.key : (t[defaultSpecies as keyof typeof t] as string);
          setSpeciesDisplayName(displayName);
        } else {
          // Fallback to translation
          setSpeciesDisplayName(t[defaultSpecies as keyof typeof t] as string || defaultSpecies);
        }
      } catch (error) {
        console.error('Failed to load species name:', error);
        setSpeciesDisplayName(t[defaultSpecies as keyof typeof t] as string || defaultSpecies);
      }
    };
    loadSpeciesName();
  }, [defaultSpecies, t]);

  // Helper function to translate fishing factors
  const translateFactor = (factor: FishingFactor): string => {
    const translationKey = factor.key as keyof typeof t;
    let text = t[translationKey] as string || factor.key;
    
    // Append values after the text if present
    if (factor.values) {
      const valueStrings: string[] = [];
      
      if (factor.values.speed !== undefined) {
        valueStrings.push(`${factor.values.speed} m/s`);
      }
      if (factor.values.gusts !== undefined) {
        valueStrings.push(`byar ${factor.values.gusts} m/s`);
      }
      if (factor.values.temp !== undefined) {
        valueStrings.push(`~${factor.values.temp}°C`);
      }
      if (factor.values.amount !== undefined) {
        valueStrings.push(`${factor.values.amount} mm/h`);
      }
      if (factor.values.distance !== undefined) {
        valueStrings.push(`${factor.values.distance} km`);
      }
      if (factor.values.percent !== undefined) {
        valueStrings.push(`-${factor.values.percent}%`);
      }
      if (factor.values.trend6h !== undefined) {
        valueStrings.push(`${factor.values.trend6h} hPa/6h`);
      }
      if (factor.values.trend24h !== undefined) {
        valueStrings.push(`24h: ${factor.values.trend24h} hPa`);
      }
      
      if (valueStrings.length > 0) {
        text = `${text} (${valueStrings.join(', ')})`;
      }
    }
    
    return `${factor.icon} ${text}`;
  };

  // Use today's score from dailyFishingScores to ensure consistency with the graph
  const getTodaysScore = (): FishingScore => {
    // ALWAYS use daily score from backend for consistency
    // Daily scores are calculated the same way on backend
    
    // Find which daily weather entry matches selectedDate
    const selectedDayIndex = dailyWeather.findIndex(day => day.date === selectedDate);
    
    if (selectedDayIndex >= 0 && selectedDayIndex < dailyFishingScores.length) {
      const todaysScore = dailyFishingScores[selectedDayIndex];
      console.log(`🎯 Using daily score for ${selectedDate} (index ${selectedDayIndex}):`, {
        score: todaysScore.score,
        factorsCount: todaysScore.factors?.length || 0,
        criticalCount: todaysScore.criticalFactors?.length || 0
      });
      return todaysScore;
    }

    // Fallback if not found (should never happen)
    console.warn(`⚠️ Could not find daily score for ${selectedDate}, using fallback`);
    
    if (!fishingScores || fishingScores.length === 0) {
      return { 
        score: 0, 
        factors: [], 
        criticalFactors: [],
        debug: {
          sLight: '', sPress: '', sWind: '', sPC: '', sMoon: '', sSea: '', sStab: '',
          frontMul: '', waterTemp: '', trend6h: '', trend24h: '', hourIndex: 0, cloudiness: ''
        }
      };
    }

    // Calculate from hourly as last resort
    console.log('🎯 Calculating today\'s score from hourly scores (fallback)');

    const activeHourScores = fishingScores.filter((_, index) => index >= 4 && index <= 20);
    const nightHourScores = fishingScores.filter((_, index) => index < 4 || index > 20);
    
    if (activeHourScores.length === 0) {
      // Fallback to average of all hours
      const avgScore = fishingScores.reduce((sum, fs) => sum + fs.score, 0) / fishingScores.length;
      return { ...fishingScores[12], score: Math.round(avgScore) };
    }
    
    // Calculate weighted average (80% active, 20% night)
    const activeAvg = activeHourScores.reduce((sum, fs) => sum + fs.score, 0) / activeHourScores.length;
    const nightAvg = nightHourScores.length > 0
      ? nightHourScores.reduce((sum, fs) => sum + fs.score, 0) / nightHourScores.length
      : activeAvg * 0.5;
    
    const dailyScore = Math.round(activeAvg * 0.8 + nightAvg * 0.2);
    
    // Find the hour that best represents this daily score (prefer midday hours)
    const middayScores = activeHourScores.filter((_, idx) => idx >= 8 && idx <= 14);
    const representativeScore = middayScores.length > 0
      ? middayScores.reduce((best, current) => 
          Math.abs(current.score - dailyScore) < Math.abs(best.score - dailyScore) ? current : best
        )
      : activeHourScores[0];

    console.log('🎣 Representative score:', {
      score: dailyScore,
      factors: representativeScore.factors,
      criticalFactors: representativeScore.criticalFactors
    });

    return {
      ...representativeScore,
      score: dailyScore
    };
  };

  const currentScore = getTodaysScore();

  // Hourly Chart Effect
  useEffect(() => {
    if (!chartRef.current || !fishingScores || fishingScores.length === 0) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const hourlyLabels = fishingScores.map((_, index) => `${index}:00`);
    const hourlyScores = fishingScores.map(d => d.score);

    // Color each point based on score
    const pointColors = hourlyScores.map(score => {
      if (score >= 70) return 'rgba(34, 197, 94, 0.8)'; // Green
      if (score >= 50) return 'rgba(250, 204, 21, 0.8)'; // Yellow
      if (score >= 30) return 'rgba(251, 146, 60, 0.8)'; // Orange
      return 'rgba(239, 68, 68, 0.8)'; // Red
    });

    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, 'rgba(34, 211, 238, 0.4)');
    gradient.addColorStop(1, 'rgba(34, 211, 238, 0.05)');

    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: hourlyLabels,
        datasets: [{
          label: t.fishingScore,
          data: hourlyScores,
          borderColor: 'rgb(34, 211, 238)',
          backgroundColor: gradient,
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            titleColor: '#fff',
            bodyColor: '#cbd5e1',
            borderColor: 'rgba(34, 211, 238, 0.5)',
            borderWidth: 1,
            padding: 12
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#94a3b8', font: { size: 10 } }
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#94a3b8', font: { size: 10 } }
          }
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [fishingScores, t.fishingScore]);

  // Daily Chart Effect
  useEffect(() => {
    if (!dailyChartRef.current || !dailyFishingScores || dailyFishingScores.length === 0) return;

    if (dailyChartInstanceRef.current) {
      dailyChartInstanceRef.current.destroy();
    }

    const ctx = dailyChartRef.current.getContext('2d');
    if (!ctx) return;

    // Generate labels: 5 days backward + selected date + 5 days forward
    const dailyLabels = dailyFishingScores.map((_, index) => {
      const date = new Date(selectedDate);
      date.setDate(date.getDate() - 5 + index); // Start 5 days before selected date
      return date.toLocaleDateString('sv-SE', { weekday: 'short', month: 'short', day: 'numeric' });
    });
    const dailyScores = dailyFishingScores.map(d => d.score);

    // Find which index corresponds to the selected date
    const selectedDayIndex = dailyFishingScores.findIndex((_, index) => {
      const date = new Date(selectedDate);
      date.setDate(date.getDate() - 5 + index);
      const dateStr = date.toISOString().split('T')[0];
      return dateStr === selectedDate;
    });
    const highlightIndex = selectedDayIndex >= 0 ? selectedDayIndex : 5; // Fallback to 5 if not found
    
    console.log(`📅 Daily chart: selected date=${selectedDate}, highlightIndex=${highlightIndex}`);

    // Color each point based on score
    const pointColors = dailyScores.map(score => {
      if (score >= 70) return 'rgba(34, 197, 94, 0.8)'; // Green
      if (score >= 50) return 'rgba(250, 204, 21, 0.8)'; // Yellow
      if (score >= 30) return 'rgba(251, 146, 60, 0.8)'; // Orange
      return 'rgba(239, 68, 68, 0.8)'; // Red
    });

    // Make the selected date's point larger
    const pointRadii = dailyScores.map((_, index) => index === highlightIndex ? 8 : 6);

    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, 'rgba(34, 211, 238, 0.4)');
    gradient.addColorStop(1, 'rgba(34, 211, 238, 0.05)');

    dailyChartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dailyLabels,
        datasets: [{
          label: t.fishingScore,
          data: dailyScores,
          borderColor: 'rgb(34, 211, 238)',
          backgroundColor: gradient,
          tension: 0.4,
          fill: true,
          pointRadius: pointRadii,
          pointHoverRadius: 10,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            titleColor: '#fff',
            bodyColor: '#cbd5e1',
            borderColor: 'rgba(34, 211, 238, 0.5)',
            borderWidth: 1,
            padding: 12
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#94a3b8', font: { size: 10 } }
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#94a3b8', font: { size: 10 } }
          }
        }
      }
    });

    return () => {
      if (dailyChartInstanceRef.current) {
        dailyChartInstanceRef.current.destroy();
      }
    };
  }, [dailyFishingScores, selectedDate, t.fishingScore]);

  if (!currentScore) {
    return null;
  }

  const getScoreRating = (score: number): string => {
    if (score >= 80) return t.excellentFishing;
    if (score >= 60) return t.goodConditions;
    if (score >= 40) return t.moderateConditions;
    return t.difficultConditions;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-400'; // Green - matches chart
    if (score >= 50) return 'text-yellow-400'; // Yellow - matches chart
    if (score >= 30) return 'text-orange-400'; // Orange - matches chart
    return 'text-red-400'; // Red - matches chart
  };

  // Format selected date for display
  const getDateDisplay = (): string => {
    const today = new Date().toISOString().split('T')[0];
    if (selectedDate === today) {
      return t.today;
    }
    const date = new Date(selectedDate);
    return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
  };

  return (
    <div id="fishing-forecast-section">
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-900/20 to-cyan-900/20 backdrop-blur-xl border border-cyan-400/20 p-4 sm:p-6">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
              🎣 {t.fishingForecast}
            </h3>
            <div className="text-xs text-cyan-400">
              {speciesDisplayName || t[defaultSpecies as keyof typeof t] as string}
            </div>
          </div>
          <p className="text-xs text-cyan-400">{t.advancedAlgorithm}</p>
        </div>
        
        {/* Dagens fiske-score */}
        <div id="fishing-score-today" className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-400/30">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold">
              🎯 {t.fishingScore} <span id="fishing-date-display" className="text-cyan-400 text-sm font-normal">{getDateDisplay()}</span>
            </h4>
            <div id="fishing-score-display" className={`text-2xl font-bold ${getScoreColor(currentScore.score)}`}>
              {currentScore.score.toFixed(0)}
            </div>
          </div>
          <div id="fishing-analysis-text" className="text-sm text-slate-300 leading-relaxed">
            <p className={`font-semibold mb-2 ${getScoreColor(currentScore.score)}`}>{getScoreRating(currentScore.score)}</p>
            {currentScore.factors && currentScore.factors.length > 0 && (
              <div className="mt-2 text-xs">
                <span className="text-green-400">{t.positiveFactors} </span>
                <span className="text-slate-300">
                  {currentScore.factors.map(translateFactor).join(', ')}
                </span>
              </div>
            )}
            {currentScore.criticalFactors && currentScore.criticalFactors.length > 0 && (
              <div className="mt-1 text-xs">
                <span className="text-red-400">{t.negativeFactors} </span>
                <span className="text-slate-300">
                  {currentScore.criticalFactors.map(translateFactor).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Fiske-grafer */}
        <div className="space-y-4">
          {/* Timme för timme idag */}
          <div className="bg-white/[0.02] rounded-xl p-4">
            <h5 id="hourly-chart-title" className="text-white font-medium mb-3">⏰ {t.hourlyForecast}</h5>
            <div className="h-48">
              <canvas ref={chartRef} className="w-full h-full"></canvas>
            </div>
          </div>
          
          {/* Kommande dagar */}
          <div className="bg-white/[0.02] rounded-xl p-4">
            <h5 id="daily-chart-title" className="text-white font-medium mb-3">📅 {t.daysBackAndForward2.replace('{date}', getDateDisplay())}</h5>
            <div className="h-48">
              <canvas ref={dailyChartRef} className="w-full h-full"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FishingForecast;
